import { PlannerSidebarData } from './components/planner-v2/planner-v2.interface';
import { BuyOlxAdsBundlesPayload } from './components/buy-olx-ads-bundles/buy-olx-ads-bundles.interface';
import { AdvertActionPayload } from './components/advert-action/advert-action.interface';
import { BuyOlxPackagesPayload } from './components/buy-olx-packages/buy-olx-packages-v2.interface';
import type { AutomationLogDetailsPayload } from '../../../modules/crm/automation-v2/components-v2/automation-logs/automation-logs.interface';
import { ChatsSidebarPayload } from '../chat-sidebar/chat-sidebar.interface';
import {
  BoardColumn,
  BoardField,
  BoardFilterSetData,
  BoardFunnel,
  BoardManager,
  DynamicBoardCard,
} from '../../board-v3/board-v3.types';
import { ICardSectionColumn } from '../../../interfaces/card-section.interface';
import { FUNNEL_TYPE } from '../../../enum/constants';
import { CardPayment, PaymentServiceRecord } from '../../payments/payments.types';
import { TaskDetailPayload, TaskFormPayload } from '../../tasks/tasks.types';
import { NpShipmentPrefill } from '../../nova-poshta/nova-poshta.types';
import { IChannelGroup } from '../../../model/channel.model';

export interface ChannelGroupFormPayload {
  group: IChannelGroup | null;
}

export interface BoardV3CardViewSettingsPayload {
  funnelId: string | null;
  fields: BoardField[];
  activeKeys: string[];
}

export interface BoardV3QuickCreateSettingsPayload {
  funnelId: string | null;
  fields: BoardField[];
  activeKeys: string[];
  managers: BoardManager[];
}

export interface BoardV3SectionFieldsPayload {
  funnelId: string | null;
  activeColumns: ICardSectionColumn[];
}

export interface BoardV3FieldsSettingsPayload {
  funnelId: string | null;
  funnelCurrency: string;
  entityType: string;
}

export interface BoardV3StageSettingsPayload {
  funnelId: string | null;
  columns: BoardColumn[];
}

export interface BoardV3FiltersPayload {
  funnelId: string | null;
  funnelType: string;
  fields: BoardField[];
  managers: BoardManager[];
  activeData: BoardFilterSetData;
  activeFilterId: string | null;
}

export interface BoardV3FiltersSidebarResult {
  filters: BoardFilterSetData;
  filterName: string;
  filterId: string | null;
}

export interface BoardV3ImportMappingPayload {
  funnelId: string | null;
  fields: BoardField[];
  columns: BoardColumn[];
}

export interface BoardV3ExportFieldsPayload {
  funnelId: string | null;
  fields: BoardField[];
}

export interface BoardV3MoveCardPayload {
  card: DynamicBoardCard | null;
  cards?: DynamicBoardCard[];
  columns: BoardColumn[];
}

export interface BoardV3LeadConversionPayload {
  cardIds: string[];
}

export interface BoardV3MergeCardsPayload {
  cardIds: string[];
}

export interface ChannelTypesSidebarPayload {
  selectedType?: string | null;
}

export interface BoardV3LinkChatPayload {
  excludes: string[];
}

export interface BoardV3ContactFormPayload {
  cardIds: string[];
}

export interface BoardV3LinkContactPayload {
  excludes: string[];
}

export interface BoardV3ContactDetailPayload {
  contactId: string;
  canOpenCards: boolean;
}

export interface BoardV3CompanyFormPayload {
  cardIds: string[];
}

export interface BoardV3CompanyDetailPayload {
  companyV2Id: string;
  canOpenCards: boolean;
}

export interface BoardV3CallTranscriptPayload {
  callId: string | null;
}

export interface ChatTimelinePayload {
  chatId: string;
  chatTitle?: string | null;
}

export interface BoardV3PaymentFormPayload {
  /** Product total in the card currency, used only for a new payment. */
  defaultAmount?: number | null;
  confirmOnly?: boolean;
  chats?: import('../../../interfaces').IChatItem[];
  cardId: string;
  /** Currency of the card: a payment has none of its own */
  currency: string | null;
  /** The pending payment to edit, a new payment when it is not set */
  payment: CardPayment | null;
}

export interface PaymentServiceFormPayload {
  /** The service to edit, a new service when it is null */
  service: PaymentServiceRecord | null;
}

export interface NovaPoshtaShipmentFormPayload {
  cardId: string;
  /** What the new waybill starts with, from the deal */
  prefill: NpShipmentPrefill | null;
}

export interface NovaPoshtaLinkFormPayload {
  cardId: string;
}

export interface BoardV3CreateFunnelPayload {
  funnel: BoardFunnel | null;
  funnelType: FUNNEL_TYPE;
  dealFunnels: BoardFunnel[];
}

export interface SidebarData<T = SidebarPayload> {
  type: SidebarType;
  payload: T;
}

export enum SidebarType {
  Planner,
  BuyOlxAdsBundles,
  AdvertAction,
  BuyOlxPackages,
  CardV3,
  BoardV3CardViewSettings,
  BoardV3QuickCreateSettings,
  BoardV3FieldsSettings,
  BoardV3StageSettings,
  BoardV3Filters,
  BoardV3ImportMapping,
  BoardV3ExportFields,
  BoardV3MoveCard,
  BoardV3LeadConversion,
  BoardV3LinkChat,
  AutomationLogDetails,
  Chats,
  Contact,
  CreateGroup,
  InviteManager,
  Ticket,
  UpdateManager,
  CreateChannel,
  Site,
  Order,
  CardLegacy,
  CreateAds,
  ChangeAdvert,
  BuyOlxPackageLegacy,
  IntegrationCheckbox,
  IntegrationAi,
  NeuronAndAnotherCrm,
  IntegrationRingostat,
  IntegrationOlx,
  IntegrationRia,
  IntegrationTelegramBot,
  IntegrationTelegramUser,
  CreateWidget,
  IntegrationInstagram,
  IntegrationFacebook,
  IntegrationViber,
  IntegrationWhatsapp,
  IntegrationProm,
  IntegrationTiktok,
  IntegrationTelegram,
  IntegrationRingostatChannel,
  BoardV3SectionFields,
  BoardV3CallTranscript,
  BoardV3CreateFunnel,
  BoardV3MergeCards,
  BoardV3ContactForm,
  BoardV3LinkContact,
  BoardV3ContactDetail,
  BoardV3CompanyForm,
  BoardV3CompanyDetail,
  ChannelTypes,
  InviteEmployees,
  EmployeeProfile,
  WarehouseProductForm,
  WarehouseProductImport,
  WarehouseForm,
  WarehouseDocument,
  BoardV3WarehouseSettings,
  BoardV3PaymentForm,
  TaskForm,
  TaskDetail,
  PaymentServiceForm,
  NovaPoshtaSettings,
  NovaPoshtaShipmentForm,
  NovaPoshtaLinkForm,
  ChannelGroupForm,
  ChatTimeline,
  VchasnoKasaSettings,
  VchasnoKasaRegisterForm,
  VchasnoKasaOperationDetail,
  VchasnoKasaHistory,
  VchasnoKasaReturn,
}

export type SidebarPayload =
  | PlannerSidebarData
  | BuyOlxAdsBundlesPayload
  | AdvertActionPayload
  | BuyOlxPackagesPayload
  | AutomationLogDetailsPayload
  | ChatsSidebarPayload
  | BoardV3CardViewSettingsPayload
  | BoardV3QuickCreateSettingsPayload
  | BoardV3SectionFieldsPayload
  | BoardV3FieldsSettingsPayload
  | BoardV3StageSettingsPayload
  | BoardV3FiltersPayload
  | BoardV3ImportMappingPayload
  | BoardV3ExportFieldsPayload
  | BoardV3MoveCardPayload
  | BoardV3LeadConversionPayload
  | BoardV3MergeCardsPayload
  | ChannelTypesSidebarPayload
  | ChannelGroupFormPayload
  | BoardV3LinkChatPayload
  | BoardV3CallTranscriptPayload
  | ChatTimelinePayload
  | BoardV3CreateFunnelPayload
  | BoardV3ContactFormPayload
  | BoardV3LinkContactPayload
  | BoardV3ContactDetailPayload
  | BoardV3CompanyFormPayload
  | BoardV3CompanyDetailPayload
  | BoardV3PaymentFormPayload
  | PaymentServiceFormPayload
  | NovaPoshtaShipmentFormPayload
  | NovaPoshtaLinkFormPayload
  | TaskFormPayload
  | TaskDetailPayload;
